import { Controller } from "@hotwired/stimulus"
import { icone } from "../lib/icones"

export default class extends Controller {
    static targets = ["trigger", "form", "textPanel", "imagePanel", "linkPanel", "pollPanel", "pollOptions", "postType", "fileInput", "imagePreview", "formElement", "cloudflareUrlsInput", "submitBtn", "linkInput", "imageBtn", "linkBtn", "pollBtn", "bodyInput", "titleInput", "titleCounter", "bodyCounter"]

    static TITLE_MAX = 120
    static BODY_MAX  = 2000

    connect() {
        this.uploadedUrls = []
        this.pendingUploads = 0
        this._overLimit = false
        this.csrfToken = document.querySelector('meta[name="csrf-token"]').content
        this.optionIndex = 2 // Starts at 2 because index 0 and 1 are already in HTML
    }

    expand() {
        this.triggerTarget.classList.add("hidden")
        this.formTarget.classList.remove("hidden")
        if (this.hasBodyInputTarget) setTimeout(() => this.bodyInputTarget.focus(), 50)
    }

    expandWithImage(e) {
        e.stopPropagation()
        this.expand()
        this.imagePanelTarget.classList.remove("hidden")
        this.linkPanelTarget.classList.add("hidden")
        this.pollPanelTarget.classList.add("hidden")
        this.postTypeTarget.value = "image"
        this._clearBtnActive()
        this._setBtnActive(this.imageBtnTarget, "text-purple-600", "bg-purple-50")
    }

    expandWithLink(e) {
        e.stopPropagation()
        this.expand()
        this.linkPanelTarget.classList.remove("hidden")
        this.imagePanelTarget.classList.add("hidden")
        this.pollPanelTarget.classList.add("hidden")
        this.postTypeTarget.value = "link"
        this._clearBtnActive()
        this._setBtnActive(this.linkBtnTarget, "text-indigo-600", "bg-indigo-50")
    }

    handleSuccess(event) {
        if (event.detail.success) {
            this.formElementTarget.reset()
            this.resetPanels()
            this.collapse()
        }
    }

    collapse() {
        this.formTarget.classList.add("hidden")
        this.triggerTarget.classList.remove("hidden")
        this.resetPanels()
    }

    resetPanels() {
        this.imagePanelTarget.classList.add("hidden")
        this.linkPanelTarget.classList.add("hidden")
        this.pollPanelTarget.classList.add("hidden")
        this.postTypeTarget.value = "text"
        this._clearBtnActive()

        // basic cleanup
        while (this.imagePreviewTarget.firstChild) {
            this.imagePreviewTarget.removeChild(this.imagePreviewTarget.firstChild)
        }
        this.uploadedUrls = []
        this._overLimit = false
        this.updateHiddenField()
        if (this.hasLinkInputTarget) this.linkInputTarget.value = ""
        if (this.hasTitleCounterTarget) this.titleCounterTarget.classList.add("hidden")
        if (this.hasBodyCounterTarget)  this.bodyCounterTarget.classList.add("hidden")
    }

    toggleImagePanel() {
        const isHidden = this.imagePanelTarget.classList.toggle("hidden")
        if (!isHidden) {
            this.postTypeTarget.value = "image"
            this.linkPanelTarget.classList.add("hidden")
            this.pollPanelTarget.classList.add("hidden")
            this._clearBtnActive()
            this._setBtnActive(this.imageBtnTarget, "text-purple-600", "bg-purple-50")
        } else {
            this.postTypeTarget.value = "text"
            this._clearBtnActive()
        }
    }

    toggleLinkPanel() {
        const isHidden = this.linkPanelTarget.classList.toggle("hidden")
        if (!isHidden) {
            this.postTypeTarget.value = "link"
            this.imagePanelTarget.classList.add("hidden")
            this.pollPanelTarget.classList.add("hidden")
            this._clearBtnActive()
            this._setBtnActive(this.linkBtnTarget, "text-indigo-600", "bg-indigo-50")
        } else {
            this.postTypeTarget.value = "text"
            this._clearBtnActive()
        }
    }

    togglePollPanel() {
        const isHidden = this.pollPanelTarget.classList.toggle("hidden")
        if (!isHidden) {
            this.postTypeTarget.value = "poll"
            this.imagePanelTarget.classList.add("hidden")
            this.linkPanelTarget.classList.add("hidden")
            this._clearBtnActive()
            this._setBtnActive(this.pollBtnTarget, "text-emerald-600", "bg-emerald-50")
        } else {
            this.postTypeTarget.value = "text"
            this._clearBtnActive()
        }
    }

    removePoll() {
        this.pollPanelTarget.classList.add("hidden")
        this.postTypeTarget.value = "text"
        this._clearBtnActive()
    }

    _setBtnActive(btn, colorClass, bgClass) {
        btn.classList.remove("text-gray-400")
        btn.classList.add(colorClass, bgClass)
    }

    _clearBtnActive() {
        const pairs = [
            [this.imageBtnTarget, "text-purple-600", "bg-purple-50"],
            [this.linkBtnTarget,  "text-indigo-600", "bg-indigo-50"],
            [this.pollBtnTarget,  "text-emerald-600", "bg-emerald-50"],
        ]
        pairs.forEach(([btn, color, bg]) => {
            btn.classList.remove(color, bg)
            btn.classList.add("text-gray-400")
        })
    }

    addPollOption() {
        if (this.pollOptionsTarget.children.length >= 10) {
            alert("Vous ne pouvez pas ajouter plus de 10 options.")
            return
        }

        const index = this.optionIndex++
        const html = `
            <div class="flex items-center gap-2 group poll-option-row">
                <input type="text" name="feed_post[poll_options_attributes][${index}][text]" placeholder="Option ${this.pollOptionsTarget.children.length + 1}" class="flex-1 rounded-xl border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 text-sm">
                <input type="hidden" name="feed_post[poll_options_attributes][${index}][position]" value="${index}" class="poll-position">
                <button type="button" data-action="click->feed-composer#removePollOption" class="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100">
                    ${icone("x")}
                </button>
            </div>
        `
        this.pollOptionsTarget.insertAdjacentHTML('beforeend', html)
        this.updateOptionPlaceholders()
    }

    removePollOption(e) {
        if (this.pollOptionsTarget.children.length <= 2) {
            alert("Un sondage doit comporter au moins deux options.")
            return
        }

        e.currentTarget.closest('.poll-option-row').remove()
        this.updateOptionPlaceholders()
    }

    updateOptionPlaceholders() {
        const rows = this.pollOptionsTarget.querySelectorAll('.poll-option-row')
        rows.forEach((row, idx) => {
            const input = row.querySelector('input[type="text"]')
            if (input) input.placeholder = `Option ${idx + 1}`

            const position = row.querySelector('.poll-position')
            if (position) position.value = idx
        })
    }

    async previewImages(e) {
        const files = Array.from(e.target.files)
        if (files.length === 0) return

        this.pendingUploads += files.length
        this.updateSubmitButton()

        files.forEach(file => {
            // 1. Create preview element with loading state
            const previewId = `upload-${Math.random().toString(36).substr(2, 9)}`
            const wrapper = this.createPreviewElement(previewId)
            this.imagePreviewTarget.appendChild(wrapper)

            // 2. Local preview
            const reader = new FileReader()
            reader.onload = (event) => {
                const img = wrapper.querySelector("img")
                img.src = event.target.result
            }
            reader.readAsDataURL(file)

            // 3. Cloudflare upload
            this.uploadFile(file, previewId, wrapper)
        })
    }

    createPreviewElement(id) {
        const wrapper = document.createElement("div")
        wrapper.id = id
        wrapper.className = "relative w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden border border-gray-200 group animate-pulse bg-gray-100"

        wrapper.innerHTML = `
            <img src="" class="w-full h-full object-cover" />
            <div class="absolute inset-0 bg-black/20 flex items-center justify-center loading-overlay">
                <svg class="w-6 h-6 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
            <button type="button" data-action="click->feed-composer#removeImage" data-id="${id}" class="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
        `
        return wrapper
    }

    async uploadFile(file, id, wrapper) {
        try {
            // Get upload URL
            const response = await fetch('/image_uploads/create_upload_url', {
                method: "POST",
                headers: { "X-CSRF-Token": this.csrfToken, "Accept": "application/json" }
            })
            const data = await response.json()
            if (!data.success) throw new Error(data.error)

            // Upload to Cloudflare
            const formData = new FormData()
            formData.append("file", file)
            const cfResponse = await fetch(data.upload_url, { method: "POST", body: formData })
            const cfData = await cfResponse.json()
            if (!cfData.success) throw new Error("CF failed")

            // Success
            const result = cfData.result
            let url = result.variants.find(v => v.endsWith('public')) || result.variants[0]

            wrapper.dataset.url = url
            wrapper.classList.remove("animate-pulse", "bg-gray-100")
            wrapper.querySelector(".loading-overlay").remove()

            this.uploadedUrls.push(url)
            this.updateHiddenField()
        } catch (error) {
            console.error(error)
            wrapper.remove()
            alert("Erreur lors de l'upload d'une image")
        } finally {
            this.pendingUploads--
            this.updateSubmitButton()
        }
    }

    removeImage(e) {
        const id = e.currentTarget.dataset.id
        const wrapper = document.getElementById(id)
        if (!wrapper) return

        const url = wrapper.dataset.url
        if (url) {
            this.uploadedUrls = this.uploadedUrls.filter(u => u !== url)
            this.updateHiddenField()
        }

        wrapper.remove()
    }

    updateHiddenField() {
        this.cloudflareUrlsInputTarget.value = JSON.stringify(this.uploadedUrls)
    }

    checkLimits() {
        const titleLen = this.hasTitleInputTarget ? this.titleInputTarget.value.length : 0
        const bodyLen  = this.hasBodyInputTarget  ? this.bodyInputTarget.value.length  : 0

        this._updateCounter(this.hasTitleCounterTarget ? this.titleCounterTarget : null, titleLen, this.constructor.TITLE_MAX)
        this._updateCounter(this.hasBodyCounterTarget  ? this.bodyCounterTarget  : null, bodyLen,  this.constructor.BODY_MAX)

        this._overLimit = titleLen > this.constructor.TITLE_MAX || bodyLen > this.constructor.BODY_MAX
        this.updateSubmitButton()
    }

    _updateCounter(el, len, max) {
        if (!el) return
        el.textContent = `${len} / ${max}`
        el.classList.remove("hidden", "text-gray-500", "text-amber-500", "text-red-500")
        if (len === 0) {
            el.classList.add("hidden")
        } else if (len > max) {
            el.classList.add("text-red-500")
        } else if (len >= max * 0.9) {
            el.classList.add("text-amber-500")
        } else {
            el.classList.add("text-gray-500")
        }
    }

    updateSubmitButton() {
        if (this.hasSubmitBtnTarget) {
            const blocked = this.pendingUploads > 0 || !!this._overLimit
            this.submitBtnTarget.disabled = blocked
            this.submitBtnTarget.classList.toggle("opacity-50", blocked)
            this.submitBtnTarget.classList.toggle("cursor-not-allowed", blocked)
        }
    }
}
